import type { EntityData } from '@adornis/baseql/entities/types.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import type { GenericFieldFormController } from '@adornis/cms/client/generic-field-formcontroller.js';
import '@adornis/internationalization/client/x-tel-input.js';
import type { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { html, nothing } from 'lit';
import type { BHTranslationDictionary } from '../../translations/translation.js';
import type { RegistrationUserDataPlugin } from '../plugin-types.js';
import {
  IKHInstitutionEnum,
  IKHInvoiceReceiverEnum,
  IKHOccupationEnum,
  type IKHStammdaten,
} from '../stammdaten/ikh-stammdaten-plugin.js';
import { InternalInfoBanner } from './helpers.js';

type stammdatenKeys = keyof EntityData<IKHStammdaten>;

export const ikhRegistrationUserDataPlugin: RegistrationUserDataPlugin<IKHStammdaten> = {
  id: 'registration::user-data::ikh',
  type: 'registration::user-data',
  render: (
    stammdatenController: GenericFieldFormController<IKHStammdaten>,
    translationController: TranslationController<BHTranslationDictionary>,
  ) => {
    const genericFieldOption = (fieldName: string) =>
      ({
        placeholder: translationController.translate('ikh', 'field', fieldName as any) as string,
        placeholderMode: 'static-floating',
      } as const);

    return html`
      <x-flex space="lg">
        <x-flex space="sm">
          <x-tel-input
            ${stammdatenController.field('phoneNumber')}
            placeholder=${translationController.translate('field', 'phone')}
          ></x-tel-input>

          ${(['salutation', 'title', 'occupation'] as stammdatenKeys[]).map(fieldName =>
            stammdatenController.genericField([fieldName], genericFieldOption(fieldName)),
          )}
          ${stammdatenController.document?.occupation === IKHOccupationEnum.IKHHESS_HOCHSCHULANGEHOERIGE
            ? html`${stammdatenController.genericField(
                ['hessHochschulDepartment'],
                genericFieldOption('hessHochschulDepartment'),
              )}`
            : html`
                ${stammdatenController.genericField(['department'], genericFieldOption('department'))}
                ${stammdatenController.document?.department === IKHInstitutionEnum.IKHEXTERN
                  ? ['externalDepartmentUniversity', 'externalDepartmentDepartment'].map(fieldName =>
                      stammdatenController.genericField([fieldName], genericFieldOption(fieldName)),
                    )
                  : nothing}
                ${stammdatenController.document?.department === IKHInstitutionEnum.IKHOTHERS
                  ? ['otherDepartmentFurtherInformation'].map(fieldName =>
                      stammdatenController.genericField([fieldName], genericFieldOption(fieldName)),
                    )
                  : nothing}
              `}
        </x-flex>
        <x-flex space="md">
          <tr-h4> ${translationController.translate('field', 'address', 'billingAddress')} </tr-h4>
          <x-flex space="sm">
            ${stammdatenController.document?.invoiceReceiver === IKHInvoiceReceiverEnum.IKHINTERNAL
              ? InternalInfoBanner()
              : nothing}
            ${stammdatenController.genericField(['invoiceReceiver'], genericFieldOption('invoiceReceiver'))}
            ${stammdatenController.document?.invoiceReceiver === IKHInvoiceReceiverEnum.IKHEXTERNAL
              ? html`${stammdatenController.genericField(['institution'], genericFieldOption('institution'))}
                ${stammdatenController.genericField(['institutionEmail'], genericFieldOption('institutionEmail'))}`
              : nothing}
            ${stammdatenController.document?.invoiceReceiver === IKHInvoiceReceiverEnum.IKHINTERNAL
              ? html`
                  ${(['division', 'postbox', 'costCenter'] as stammdatenKeys[]).map(fieldName =>
                    stammdatenController.genericField([fieldName], genericFieldOption(fieldName)),
                  )}
                `
              : nothing}
            ${stammdatenController.document?.invoiceReceiver === IKHInvoiceReceiverEnum.IKHEXTERNAL
              ? stammdatenController.genericField(['attention'], genericFieldOption('attention'))
              : nothing}
            ${stammdatenController.document?.invoiceReceiver === IKHInvoiceReceiverEnum.IKHINTERNAL
              ? nothing
              : html`
                  ${stammdatenController.genericField(
                    ['address', 'streetAndHousenumber'],
                    genericFieldOption('streetAndHousenumber'),
                  )}
                  <x-flex horizontal space="sm" ${acss({ '*:last-child': { flex: '1' } })}>
                    ${stammdatenController.genericField(['address', 'zipCity', 'zip'], genericFieldOption('zip'))}
                    ${stammdatenController.genericField(['address', 'zipCity', 'city'], genericFieldOption('city'))}
                  </x-flex>
                `}
          </x-flex>
        </x-flex>
      </x-flex>
    `;
  },
};
